import React, { useMemo } from 'react';
import { Alert, Button, Select } from 'antd';

import { ARROW_RIGHT } from '../icons';
import { optionSearchFilter } from '../Input';
import { getDataTypeMeta } from '../../utils/dictionary';
import { asksDateOrder } from '../../utils/spreadsheet/rows';
import { columnSuffix, elementLabel, headerChoices, sampleValues, unfilledRequired } from '../../utils/spreadsheet/columns';
import { CHOOSE_ELEMENT, LEAVE_OUT } from './importCopy';

const LEFT_OUT = '';

const typeWords = el => getDataTypeMeta(el?.dataType || 'text-small')?.label || 'Short text';

const DATE_ORDERS = [
	{ value: 'mdy', label: 'Month first, 3/14/2026' },
	{ value: 'dmy', label: 'Day first, 14/3/2026' },
	{ value: 'ymd', label: 'Year first, 26/3/14' },
];

function ColumnRow({ sheet, column, entry, suggestion, elementsByName, options, dateDefault, onMap, onAccept }) {
	const samples = useMemo(() => sampleValues(sheet, column.index), [sheet, column.index]);
	const element = entry?.element ? elementsByName[entry.element] : null;
	const asksOrder = asksDateOrder(sheet, column, element);
	let value;
	if (entry?.element) value = entry.element;
	else if (entry?.left) value = LEFT_OUT;
	const suggested = !entry && suggestion ? elementsByName[suggestion.element] : null;
	return (
		<li className="spreadsheet-import__column">
			<div className="spreadsheet-import__source">
				<span className="spreadsheet-import__column-name">{column.name}</span>
				<span className="spreadsheet-import__samples">{samples.length ? samples.join(' · ') : 'Empty'}</span>
			</div>
			<ARROW_RIGHT aria-hidden="true" className="spreadsheet-import__arrow" />
			<div className="spreadsheet-import__target">
				<Select
					showSearch
					placeholder={CHOOSE_ELEMENT}
					value={value}
					options={options(column)}
					filterOption={optionSearchFilter}
					onChange={next => onMap(column.index, next === LEFT_OUT
						? { left: true }
						: { element: next, suffix: columnSuffix(column.name, elementsByName[next]) })}
					aria-label={`The element for ${column.name}`}
				/>
				{suggested ? (
					<span className="spreadsheet-import__suggested">
						{`Suggested: ${elementLabel(suggested)}`}
						<Button size="small" type="link" onClick={() => onAccept(column.index)} aria-label={`Accept ${elementLabel(suggested)} for ${column.name}`}>Accept</Button>
					</span>
				) : null}
				{entry?.suffix ? <span className="spreadsheet-import__note">{`Read in ${entry.suffix}`}</span> : null}
				{asksOrder ? (
					<label className="spreadsheet-import__field">
						<span className="micro-label">How the dates are written</span>
						<Select
							value={entry.order || dateDefault}
							options={DATE_ORDERS}
							onChange={order => onMap(column.index, { ...entry, order })}
							aria-label={`How the dates in ${column.name} are written`}
						/>
					</label>
				) : null}
			</div>
		</li>
	);
}

/**
 * Step 2: which column fills which element. Suggestions are offered and never
 * applied (platform-standards §7, Mapping one card onto another): a notice with
 * Accept all, and Suggested on the row. A column is left out until the person
 * says otherwise, and each element takes one column. keepr's reading of which
 * row names the columns is a reading, so the person can change it.
 */
export default function MatchColumnsStep({ sheet, elements, withheld, mapping, suggestions, dateDefault, onHeader, onMap, onAccept, onAcceptAll }) {
	const elementsByName = useMemo(() => Object.fromEntries(elements.map(el => [el.name, el])), [elements]);
	const takenBy = useMemo(() => {
		const out = {};
		sheet.columns.forEach(column => {
			const name = mapping[column.index]?.element;
			if (name) out[name] = column;
		});
		return out;
	}, [mapping, sheet.columns]);

	const options = column => [
		{ value: LEFT_OUT, label: LEAVE_OUT, searchText: LEAVE_OUT },
		...elements.map(el => {
			const taker = takenBy[el.name];
			const taken = Boolean(taker) && taker.index !== column.index;
			return {
				value: el.name,
				disabled: taken,
				searchText: elementLabel(el),
				label: (
					<span className="spreadsheet-import__option">
						<span>{elementLabel(el)}</span>
						<span className="spreadsheet-import__option-type">{taken ? `Used by ${taker.name}` : typeWords(el)}</span>
					</span>
				),
			};
		}),
	];

	const matched = Object.keys(takenBy).length;
	const suggestionCount = Object.keys(suggestions).length;
	const unfilled = unfilledRequired(elements, mapping);
	return (
		<section className="spreadsheet-import__panel" aria-label="Match columns">
			<label className="spreadsheet-import__field">
				<span className="micro-label">Column names are in</span>
				<Select
					value={sheet.headerRow}
					options={headerChoices(sheet.raw || [])}
					onChange={onHeader}
					aria-label="The row with the column names"
				/>
				{sheet.headerRow > 1 ? <span className="spreadsheet-import__note">Rows above it stay out.</span> : null}
			</label>
			{suggestionCount ? (
				<Alert
					type="info"
					showIcon
					message={suggestionCount === 1 ? '1 column matches by name.' : `${suggestionCount} columns match by name.`}
					action={<Button size="small" onClick={onAcceptAll}>Accept all</Button>}
				/>
			) : null}
			<p className="spreadsheet-import__summary" aria-live="polite">
				{matched
					? `${matched} of ${sheet.columns.length} columns matched. The rest stay out.`
					: 'Choose an element for each column you want to bring in.'}
			</p>
			<ul className="spreadsheet-import__columns">
				{sheet.columns.map(column => (
					<ColumnRow
						key={column.index}
						sheet={sheet}
						column={column}
						entry={mapping[column.index]}
						suggestion={suggestions[column.index]}
						elementsByName={elementsByName}
						options={options}
						dateDefault={dateDefault}
						onMap={onMap}
						onAccept={onAccept}
					/>
				))}
			</ul>
			{unfilled.length ? (
				<p className="spreadsheet-import__note">
					{`Required, and no column fills it yet: ${unfilled.map(elementLabel).join(', ')}.`}
				</p>
			) : null}
			{withheld.length ? (
				<p className="spreadsheet-import__note">
					{`Not offered here: ${withheld.map(({ element, why }) => `${elementLabel(element)} (${why})`).join(', ')}.`}
				</p>
			) : null}
		</section>
	);
}
